import { applyResourceValues, type ResourceValues, type ResourceColumn } from '../janus-engine/resources/resource-adjust';
// inventory-edit-form — client-side reconstruction of
// options/tools/inventory/forms/form_modify_inventory_inc.php, painted from the full-nested
// CasterCache inventory node so opening the editor needs NO server call at all (the queryData() DB-form
// render is gone for the common case). The dynamic <select>s are LAZY (data-preload-full="false"): their
// catalog loads from get_items_for_dynamic_select.php only when the user focuses the dropdown — so the
// form OPEN is fully client-side. The only other server calls are MUTATIONS, each the existing
// inventory-actions handler/endpoint: Save / field change → modifyInventory(event) (reads the inputs by id),
// privacy → queryData(); Delete uses the browser's prewrite boundary.
//
// FIDELITY: every element id / class / data-attribute that modifyInventory() reads on
// save, and that initializeTomSelect()/initializeQuillEditors() hydrate, is reproduced 1:1 from the
// PHP render_* helpers. This is now the ONLY edit form (the PHP form_modify_inventory_inc.php is retired):
// openEditInventoryForm always client-renders. The handful of encounter-possessed extras that aren't in
// the cache (the cross-encounter link picker + the map X/Z bounds) load via one lazy JSON data fetch
// (get_inventory_encounter_link_options.php) — a data fetch, not a form render. Everything else (incl.
// map_marker gating and the lockpick arc) renders from the cached blob. Enchantment validity is wired by
// loadPostFunctions()'s initInventoryModifyFormEnchantmentValidation.

import { encounterAuthoringActive } from '../shared/encounter-surface';
import { inventoryField, itemsCol, layerOf, hasCampaignPermission, catalogIdentity, posCoord } from '../shared/array-types';
import { languageNameById } from '../shared/language-catalog';
import { queueInventoryFlag, readInventoryHidden, type Bit } from '../janus-engine/inventory/flag-adjust';
import { createVitalityReader } from '../janus-engine/compile/vitality-reader';
import { inventoryTemplateVitalityMaximum, saveInventoryVitalityBaseline } from '../shared/inventory-vitality-edit';
import { renderEncounterLinkInputs, renderKeyLinkInputs } from './link-picker';
import { requireInventoryFloor } from '../janus-engine/compile/inventory-facet';
import { ARC_MIN as LOCKPICK_ARC_MIN_DEG, ARC_MAX as LOCKPICK_ARC_MAX_DEG,
    KNIFE_DEAD_START as LOCKPICK_KNIFE_DEAD_START, KNIFE_DEAD_END as LOCKPICK_KNIFE_DEAD_END,
    intervalCrossesDeadZone } from '../janus-engine/minigames/lockpick';

const LOCKPICKING_ROLL_ID = 'CC2CEB6B-CEFB-481F-911A-A47A427D5EB2'; // RollType::getId('lockpicking_roll')

export interface InventoryEditCtx {
    caster_id?: string;
    location_id?: string;
    encounter_id?: string;
    campaign_id?: string;
}

export interface InventoryEditValues {
    v_current: number;
    v_max: number;
    lockpick_degree_start: number | null;
    lockpick_degree_end: number | null;
}

/** Called by every edit save before posting; works only on this form's held controls. */
function prepareInventoryEditValues(container: HTMLElement): InventoryEditValues | null {
    const current = container.querySelector<HTMLInputElement>('#inventory_v_current');
    const maximum = container.querySelector<HTMLInputElement>('#inventory_v_max');
    const v_max = parseInt(maximum?.value ?? '', 10) || 0;
    const v_current = Math.min(parseInt(current?.value ?? '', 10) || 0, v_max);
    let lockpick_degree_start: number | null = null;
    let lockpick_degree_end: number | null = null;
    const start = container.querySelector<HTMLInputElement>('#lockpick_degree_start');
    const end = container.querySelector<HTMLInputElement>('#lockpick_degree_end');
    // An absent pair leaves the stored arc alone, including forms without arc controls.
    if (start && end && start.value !== '' && end.value !== '') {
        const a = parseFloat(start.value), b = parseFloat(end.value);
        if (!Number.isFinite(a) || !Number.isFinite(b)) {
            displayWarning('inventory-edit:invalid-lockpick-arc');
            return null;
        }
        const clamp = (value: number): number => Math.max(LOCKPICK_ARC_MIN_DEG,
            Math.min(LOCKPICK_ARC_MAX_DEG, Math.round(value * 100) / 100));
        lockpick_degree_start = clamp(a);
        lockpick_degree_end = clamp(b);
        if (lockpick_degree_start >= lockpick_degree_end) {
            displayWarning(`Lockpick sweet spot: start must be less than end (degrees ${LOCKPICK_ARC_MIN_DEG}–${LOCKPICK_ARC_MAX_DEG}).`);
            return null;
        }
        if (intervalCrossesDeadZone(lockpick_degree_start, lockpick_degree_end)) {
            displayWarning(`Lockpick sweet spot cannot cross the knife wedge on the ring (about ${LOCKPICK_KNIFE_DEAD_START}°–${LOCKPICK_KNIFE_DEAD_END}°, i.e. roughly 5–7 o'clock).`);
            return null;
        }
        start.value = String(lockpick_degree_start);
        end.value = String(lockpick_degree_end);
    }
    if (current) current.value = String(v_current);
    const range = container.querySelector<HTMLInputElement>('#inventory_v_currentRange');
    if (range) range.value = String(v_current);
    return { v_current, v_max, lockpick_degree_start, lockpick_degree_end };
}

type InventoryRecord = Record<string, unknown>;
interface InventoryLinkOption {
    inventory_id?: unknown; picture_url?: unknown; label?: unknown; selected?: unknown; opposite_match?: unknown;
}
interface KeyLinkResponse extends InventoryRecord {
    links?: unknown; encounters?: unknown; truncated?: unknown; all_encounters?: unknown; all_inventory?: unknown; show_links?: unknown;
}
interface InventoryEditHost {
    FateTide?: { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> };
    initializeTomSelect?: () => void;
    ftMountIcons?: (root?: ParentNode) => void;
    modifyInventory?: (event: unknown) => unknown;
    ftBindVitalitySliderToVMax?: (maxId: string, currentId: string, labelId: string) => void;
    ftFindFullNestedItem?: (id: string) => unknown;
    ftCollectInventoryIds?: (blob: unknown) => string[];
    ftRepaintCasterFromCache?: (casterId: string) => void;
    ftRerenderOpenSideInfo?: (ids: string[]) => void;
    ftGetOpenSideInfoId?: () => string | null;
    ftRenderItemFromCache?: (id: string, container: HTMLElement) => unknown;
    ftApplyInventoryEditToCache?: (id: string, fields: Record<string, unknown>) => boolean;
    debounceInput?: (element: HTMLInputElement, callback: string) => void;
}

function recordOf(value: unknown): InventoryRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as InventoryRecord : null;
}
function asRecords(value: unknown): InventoryRecord[] {
    const values = Array.isArray(value) ? value : (recordOf(value) ? Object.values(value as InventoryRecord) : []);
    return values.map(recordOf).filter((row): row is InventoryRecord => row !== null);
}
function inventoryEditHost(): Window & InventoryEditHost {
    return window as unknown as Window & InventoryEditHost;
}

/** HTML-escape for attribute values and text content. */
function esc(v: unknown): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

/** Trusted iconMap() HTML (or '' on miss) — mirrors PHP icon_ph(). Optional `title` sets the hover tooltip. */
function icon(key: string, w?: number, h?: number, bw?: boolean, title?: string): string {
    if (!key) return '';
    try { return iconMap(key, w, h, bw, title); } catch { return ''; }
}

/** String value of an `inventory`-bucket column (descalared, top-level fallback). */
function inv(arr: unknown, key: string): string {
    const v = inventoryField(arr, key);
    return v === undefined || v === null ? '' : String(v);
}

/** Title-cased label from a snake_case id, mirroring ucwords_and_space(). */
function labelize(name: string): string {
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── individual field builders (each mirrors the matching PHP render_* helper) ──────────────

// render_text_field(..., floating_label:false) — single labelled text input.
function textFieldFlat(id: string, value: string, iconKey: string, placeholder: string): string {
    return `
    <div class="d-flex align-items-center justify-content-start ft-dynamic-text-muted col-12">
        <div class="pl-1 pr-2">${icon(iconKey, 25, 25, true)}</div>
        <div class="w-100">
            <input type="text" class="form-control" id="${esc(id)}" name="${esc(id)}"
                placeholder="${esc(placeholder)}" value="${esc(value)}"
                data-ft-live-inventory-edit="1">
        </div>
    </div>`;
}

// render_text_field(..., floating_label:true default) — floating-label text input.
function textFieldFloating(id: string, value: string, iconKey: string): string {
    const label = labelize(id);
    return `
    <div class="d-flex align-items-center justify-content-start ft-dynamic-text-muted col-12">
        <div class="pl-1 pr-2">${icon(iconKey, 25, 25, true)}</div>
        <div class="form-floating w-100">
            <input type="text" class="form-control" id="${esc(id)}" name="${esc(id)}"
                placeholder="&#160;" value="${esc(value)}"
                data-ft-live-inventory-edit="1">
            <label for="${esc(id)}">${esc(label)}</label>
        </div>
    </div>`;
}

// render_checkbox(...) — debounced checkbox.
function checkbox(id: string, iconKey: string, label: string, checked: boolean): string {
    return `
<div class="d-flex align-items-center my-2">
    <div class="me-2">${icon(iconKey)}</div>
    <div class="form-check d-flex align-items-center">
        <input id="${esc(id)}" class="form-check-input me-2" type="checkbox" name="${esc(id)}" value="1"
            ${checked ? 'checked' : ''} data-ft-debounce-modify-inventory="1">
        <label class="form-check-label" for="${esc(id)}">${esc(label)}</label>
    </div>
</div>`;
}

// render_slider(...) — range + mirrored number input, onchange modifyInventory(event). `extraOnInput`
// (optional) is appended to BOTH inputs' oninput handlers — used by the map X/Z sliders to push the live
// value onto the encounter widget every drag tick (the DB save still rides the debounced onchange).
function slider(id: string, label: string, iconKey: string, min: number, max: number, step: number, value: number, maxSpanId?: string, extraOnInput = ''): string {
    const maxIdAttr = maxSpanId ? ` id="${esc(maxSpanId)}"` : '';
    const extra = extraOnInput ? esc(extraOnInput) : '';
    return `
        <div class='border rounded p-2'>
            <div class='text-center'>${icon(iconKey)} ${esc(label)}</div>
            <div class='d-flex align-items-center'>
                <div class='p-1 flex-shrink-0'><span>${esc(min)}</span></div>
                <div class='p-1 flex-grow-1'>
                    <input id='${esc(id)}Range' class='form-range w-100' type='range' min='${esc(min)}' max='${esc(max)}'
                        step='${esc(step)}' value='${esc(value)}'
                        data-ft-mirror-target="${esc(id)}" data-ft-modify-inventory="1"${extra ? ' data-ft-sync-xz="1"' : ''}>
                </div>
                <div class='p-1 flex-shrink-0'><span${maxIdAttr}>${esc(max)}</span></div>
            </div>
            <div class='d-flex align-items-center justify-content-center'>
                <div>
                    <input id='${esc(id)}' class='form-control' type='number' min='${esc(min)}' max='${esc(max)}'
                        step='${esc(step)}' value='${esc(value)}'
                        data-ft-mirror-target="${esc(id)}Range" data-ft-modify-inventory="1"${extra ? ' data-ft-sync-xz="1"' : ''}>
                </div>
            </div>
        </div>`;
}

// render_textarea(...) — Quill editor container + hidden textarea (the editable essence; the optional
// AI prompt/copy buttons are omitted on the cache path — cosmetic, not read on save).
function quillTextarea(base: string, value: string): string {
    const v = esc(value);
    return `
    <div class="d-flex flex-column align-items-start justify-content-start mb-4">
        <div class="d-flex align-items-center w-100 mb-2">
            <div class="pe-2">${icon(base)}</div>
            <div class="flex-grow-1"><span class="fw-bold text-decoration-underline">${esc(labelize(base))}:</span></div>
        </div>
        <div class="w-100 quill-editor-container">
            <div class="quill-editor" id="quill_${esc(base)}" data-layer-type-name="${esc(base)}"
                data-initial-content="${v}" data-update-callback="modifyInventory(event)"></div>
            <textarea id="${esc(base)}" name="${esc(base)}" style="display: none;">${v}</textarea>
        </div>
    </div>`;
}

// render_dynamic_select(...) for an AJAX-backed type (object / language): emit the <select> shell with
// the data-attributes the Tom Select loader reads, plus the placeholder + preselected option (whose
// label we already have from the cache, so it shows correctly without any fetch). The option pool is
// LAZY — data-preload-full="false" → Tom Select loads it from get_items_for_dynamic_select.php only when
// the user focuses the dropdown, so opening the form fires ZERO server calls. Mirrors the same client-
// rendered lazy pattern used by the character-creator add_abilities selects (_tab_add_abilities_inc.php
// port in includes/js-src/character-creator/index.ts).
function dynamicSelect(opts: {
    id: string; itemType: string; preselectValue: string; preselectLabel: string; gated: boolean;
    iconTooltip?: string;
}): string {
    const placeholder = `-- SELECT ${opts.id.replace(/_/g, ' ').toUpperCase()} --`;
    const preselectedJson = opts.preselectValue ? JSON.stringify([opts.preselectValue]) : '[]';
    const gatedAttr = opts.gated ? ' data-source-gating="true"' : '';
    let options = `<option value="">${esc(placeholder)}</option>`;
    if (opts.preselectValue) {
        options += `<option value="${esc(opts.preselectValue)}" selected>${esc(opts.preselectLabel || opts.preselectValue)}</option>`;
    }
    // NOTE: deliberately NO data-form-query-item-info — on the server form that attribute made Tom Select
    // fire openItemInfoValue(selectId) → openItemInfo(<template_id>) on preselect, a full server refresh
    // keyed on the TEMPLATE id that clobbered the inventory-instance side-info card AND re-registered the
    // open side-info id as the template id (breaking the post-save cache repaint, which keys on inventory_id).
    const selectHtml = `<select class="ts-select dynamic-item-select" id="${esc(opts.id)}" name="${esc(opts.id)}"
        data-item-type="${esc(opts.itemType)}" data-homebrew="true" data-preselected="${esc(preselectedJson)}"
        data-preload-full="false" data-show-icons="true" data-thumbnail-source="icon"${gatedAttr} data-ft-modify-inventory="1">${options}</select>`;
    // Left wrapper icon — render_dynamic_select's $different_icon (default true → icon_ph($layer_value_name)).
    // The select id IS the layer_value_name, so icon(opts.id) restores the same per-field icon (item template
    // / written-in-language) the PHP form showed.
    return `<div class="d-flex align-items-center w-100 min-w-0 gap-2">`
        + `<div class="pl-2 flex-shrink-0 d-flex align-items-center">${icon(opts.id, 25, 25, true, opts.iconTooltip)}</div>`
        + `<div class="flex-grow-1 min-w-0">${selectHtml}</div></div>`;
}

// The row's real hidden state; the pair commits through the client-truth flag envelope
// (INV2 2026-09-28: the legacy hide_inventory.php / reveal_inventory.php are retired). It carries the row's holder (the commit's CAS)
// and the form's encounter (the server judges the GM against the encounter that governs the row when none is named).
const HIDDEN_STATUS_ID = '47667406-1B72-9D60-310B-D8CF6BFE397D';
/** Newest click per row: only it may repaint after its commit settles. */
const editHiddenGeneration = new Map<string, number>();
function hideButtons(inventoryId: string, hidden: boolean, possessId: string, encounterId: string): string {
    const data = `data-ft-inventory-id="${esc(inventoryId)}" data-ft-possess-id="${esc(possessId)}" data-ft-encounter-id="${esc(encounterId)}"`;
    return `
        <button id='btn_reveal_creature_${esc(inventoryId)}' type='button' class='btn btn-light border btn-sm' style='display:${hidden ? 'block' : 'none'};'
            data-ft-inventory-action="reveal" ${data}>
            ${icon('reveal', 25, 25, true)}
        </button>
        <button id='btn_hide_creature_${esc(inventoryId)}' type='button' class='btn btn-dark border btn-sm' style='display:${hidden ? 'none' : 'block'};'
            data-ft-inventory-action="hide" ${data}>
            ${icon('hidden', 25, 25, true)}
        </button>`;
}

// render_item_private_toggle_buttons('inventory', ...) — public/private flip.
function privateButtons(inventoryId: string, isPrivate: boolean): string {
    const url = `${BASE_URL}options/db_queries/update/update_privacy_inc.php`;
    return `
        <button id='inventory_public_${esc(inventoryId)}' type='button' class='btn btn-primary btn-sm' style='display:${isPrivate ? 'none' : 'block'};'
            data-ft-inventory-action="make-private" data-ft-inventory-id="${esc(inventoryId)}">
            ${icon('public')}
        </button>
        <button id='inventory_private_${esc(inventoryId)}' type='button' class='btn btn-danger btn-sm' style='display:${isPrivate ? 'block' : 'none'};'
            data-ft-inventory-action="make-public" data-ft-inventory-id="${esc(inventoryId)}">
            ${icon('private')}
        </button>`;
}

// ── lockpick sweet-spot arc (port of form_modify_inventory_inc.php 671-748) ───────────────
function lockpickSupported(arr: unknown): boolean {
    const type = String(itemsCol(arr, 'item_type') ?? '').toLowerCase();
    if (type === 'lock') return true;
    return String(layerOf(arr, 'roll_type') ?? '').toUpperCase() === LOCKPICKING_ROLL_ID;
}
/**
 * Lift a stored legacy pair onto the current ruler. Invalid or partial pairs open on editable
 * defaults, preventing range-input sanitization from silently relocating one endpoint.
 * Opening the form does not save an arc; the browser validates the pair when the form saves.
 */
function lockpickDefaults(arr: unknown): { lpS: number; lpE: number } {
    const defS = LOCKPICK_ARC_MIN_DEG + 75;
    const defaults = { lpS: defS, lpE: Math.min(LOCKPICK_ARC_MAX_DEG, defS + 70) };

    const rawS = inv(arr, 'lockpick_degree_start');
    const rawE = inv(arr, 'lockpick_degree_end');
    // Blank/whitespace storage is absent; Number(' ') would incorrectly turn it into zero.
    const isNumeric = (v: string): boolean => /^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(v);
    if (!isNumeric(rawS) || !isNumeric(rawE)) {
        return defaults;
    }

    let lpS = Number(rawS);
    let lpE = Number(rawE);
    // Rows written under the old [135,405] arc sit a turn below the current ruler. Lift both ends
    // together — same clock angles, new labels — never one end alone.
    if (lpS < LOCKPICK_ARC_MIN_DEG && lpE < LOCKPICK_ARC_MIN_DEG) {
        lpS += 360;
        lpE += 360;
    }
    const valid = Number.isFinite(lpS) && Number.isFinite(lpE)
        && lpS < lpE
        && lpS >= LOCKPICK_ARC_MIN_DEG
        && lpE <= LOCKPICK_ARC_MAX_DEG;
    return valid ? { lpS, lpE } : defaults;
}
function lockpickArc(arr: unknown): string {
    const { lpS, lpE } = lockpickDefaults(arr);
    const lo = LOCKPICK_ARC_MIN_DEG, hi = LOCKPICK_ARC_MAX_DEG;
    return `
    <div class="border rounded p-2 mb-2" id="div_lockpick_arc_editor">
        <div class="text-center mb-2">${icon('lockpick')} <span class="fw-bold">Lockpick sweet spot (degrees)</span></div>
        <p class="small text-muted mb-2">Arc ${lo}°–${hi}° (12 o'clock = 0°, CW). Sweet spot must not cross the knife wedge (~${LOCKPICK_KNIFE_DEAD_START}°–${LOCKPICK_KNIFE_DEAD_END}°, ~5–7 o'clock).</p>
        <input type="hidden" id="lockpick_degree_start" value="${esc(lpS)}">
        <input type="hidden" id="lockpick_degree_end" value="${esc(lpE)}">
        <div class="position-relative lockpick-dual-range mb-2" style="height:2.75rem;">
            <div class="lockpick-dual-range__track position-absolute top-50 start-0 end-0 translate-middle-y bg-secondary rounded-pill" style="height:8px;"></div>
            <div id="lockpick_drange_fill" class="position-absolute top-50 translate-middle-y bg-info rounded-pill" style="height:8px; left:0%; width:0%; pointer-events:none;"></div>
            <input type="range" id="lockpick_range_start" class="form-range position-absolute top-0 start-0 w-100 lockpick-dual-range-input" style="z-index:2;" min="${lo}" max="${hi}" step="0.5" value="${esc(lpS)}">
            <input type="range" id="lockpick_range_end" class="form-range position-absolute top-0 start-0 w-100 lockpick-dual-range-input" style="z-index:3;" min="${lo}" max="${hi}" step="0.5" value="${esc(lpE)}">
        </div>
        <div class="small text-muted" id="lockpick_arc_readout"></div>
    </div>`;
}
function bindLockpickArc(): void {
    const div = document.getElementById('div_lockpick_arc_editor');
    if (!div) return;
    const sync = (): void => {
        const rs = document.getElementById('lockpick_range_start') as HTMLInputElement | null;
        const re = document.getElementById('lockpick_range_end') as HTMLInputElement | null;
        const hs = document.getElementById('lockpick_degree_start') as HTMLInputElement | null;
        const he = document.getElementById('lockpick_degree_end') as HTMLInputElement | null;
        const fill = document.getElementById('lockpick_drange_fill');
        const out = document.getElementById('lockpick_arc_readout');
        if (!rs || !re || !hs || !he) return;
        let a = parseFloat(rs.value), b = parseFloat(re.value);
        if (a > b) { const t = a; a = b; b = t; rs.value = String(a); re.value = String(b); }
        hs.value = String(a); he.value = String(b);
        const mn = parseFloat(rs.min), mx = parseFloat(rs.max);
        const pct1 = ((a - mn) / (mx - mn)) * 100, pct2 = ((b - mn) / (mx - mn)) * 100;
        if (fill) { fill.style.left = pct1 + '%'; fill.style.width = Math.max(0, pct2 - pct1) + '%'; }
        if (out) out.textContent = 'Start: ' + a.toFixed(1) + '° — End: ' + b.toFixed(1) + '° (width ' + (b - a).toFixed(1) + '°)';
    };
    ['lockpick_range_start', 'lockpick_range_end'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
            el.addEventListener('input', sync);
            el.addEventListener('change', () => {
                sync();
                const host = inventoryEditHost();
                if (typeof host.modifyInventory === 'function') host.modifyInventory({ target: div });
            });
        }
    });
    sync();
}

// ── encounter extras: X/Z bounds + link picker, loaded from the JSON endpoint ─────────────
function setSliderBounds(id: string, min: unknown, max: unknown): void {
    [document.getElementById(id), document.getElementById(id + 'Range')].forEach((el) => {
        if (el) { (el as HTMLInputElement).min = String(min); (el as HTMLInputElement).max = String(max); }
    });
}
function encounterLinksMarkup(links: InventoryLinkOption[]): string {
    const options = links.map((l) => {
        const img = l && l.picture_url
            ? `<img src='${String(l.picture_url).replace(/'/g, '')}' style='max-width: 25px; max-height: 25px;'> `
            : '';
        // data-content holds renderable HTML for Tom Select; only " and & must be escaped for the attribute.
        const dataContent = (img + String(l?.label ?? '')).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        return `<option value="${esc(l?.inventory_id)}"${l?.selected ? ' selected' : ''}`
            + `${l?.opposite_match ? ' data-ft-opposite-match="1"' : ''} data-content="${dataContent}">`
            + `${esc(l?.label)}</option>`;
    }).join('');
    return `
    <div class="row gx-0 mb-3 pl-4 pt-2 pb-2 pr-2 border rounded">
        <h5>${icon('encounter_links')} Inventory Encounter Links:</h5>
        <div class="col-12">
            <select id="inventory_encounter_links" class="ts-select" title="Inventory Encounter Links" data-ft-modify-inventory="1" multiple>
                ${options}
            </select>
        </div>
    </div>`;
}
// ── key ↔ lock link picker ("Locks this key opens", item_type='key' rows only) ────────────
function keyLinksMarkup(data: KeyLinkResponse): string {
    const options = asRecords(data.links).map((l) => {
        const img = l && l.picture_url
            ? `<img src='${String(l.picture_url).replace(/'/g, '')}' style='max-width: 25px; max-height: 25px;'> `
            : '';
        // data-content holds renderable HTML for Tom Select; only " and & must be escaped for the attribute.
        const dataContent = (img + String(l?.label ?? '')).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        return `<option value="${esc(l?.inventory_id)}"${l?.selected ? ' selected' : ''} data-content="${dataContent}">`
            + `${esc(l?.label)}</option>`;
    }).join('');
    const truncNote = data.truncated
        ? `<div class="col-12 small text-warning mb-1">Result list truncated — pick a single encounter or add a name filter.</div>`
        : '';
    // Scope = a searchable ENCOUNTER select (location search), not zone numbers. Unchecked box:
    // encounters of the key's location; checked: ALL encounters under the master parent — a free
    // search, NOT limited by the links system. Empty value = every encounter shown.
    const encounterOptions = asRecords(data.encounters).map((e) =>
        `<option value="${esc(e?.encounter_id)}"${e?.selected ? ' selected' : ''}>${esc(e?.label)}</option>`,
    ).join('');
    return `
    <div class="row gx-0 mb-3 pl-4 pt-2 pb-2 pr-2 border rounded">
        <h5>${icon('key')} Locks This Key Opens:</h5>
        <div class="col-12 mb-2">
            <select id="inventory_key_link_encounter" class="ts-select" title="Filter lock candidates by encounter">
                <option value="">— All encounters shown —</option>
                ${encounterOptions}
            </select>
        </div>
        <div class="col-12 d-flex align-items-center gap-3 mb-2 flex-wrap">
            <div class="form-check mb-0">
                <input id="inventory_key_link_all_encounters" class="form-check-input" type="checkbox"${data.all_encounters ? ' checked' : ''}>
                <label class="form-check-label" for="inventory_key_link_all_encounters">Search all encounters</label>
            </div>
            <div class="form-check mb-0">
                <input id="inventory_key_link_all_inventory" class="form-check-input" type="checkbox"${data.all_inventory ? ' checked' : ''}>
                <label class="form-check-label" for="inventory_key_link_all_inventory">All item types</label>
            </div>
        </div>
        ${truncNote}
        <div class="col-12">
            <select id="inventory_key_link_locks" class="ts-select" title="Locks this key opens" data-ft-modify-inventory="1" multiple>
                ${options}
            </select>
        </div>
    </div>`;
}

/**
 * For a key row, fetch the scoped lock-candidate list from get_inventory_key_link_options.php and
 * inject the "Locks this key opens" picker. The scope controls (encounter select / all-encounters /
 * all-item-types) re-fetch this list; only the multiselect itself saves (via modifyInventory → the
 * directional inventory_key_links block in modify_inventory_inc.php). Fire-and-forget like
 * loadEncounterExtras.
 */
async function compileLinkPickerInputs(data: InventoryRecord): Promise<void> {
    const hosts = asRecords(data.inventory_hosts);
    for (const host of hosts) {
        if (!window.ftAdoptInventoryBase) throw new Error('unported:inventory-base-unavailable');
        await window.ftAdoptInventoryBase(host, { transient: true });
        requireInventoryFloor(host);
        if (!Array.isArray(host.inventory)) throw new Error('unported:inventory-base-unavailable');
    }
}

function loadKeyLinkExtras(
    inventoryId: string,
    ctx: InventoryEditCtx,
    opts: { encounter_scope_id?: string; all_encounters?: boolean; all_inventory?: boolean } = {},
): void {
    void (async () => {
        try {
            const res = await fetch(`${BASE_URL}options/tools/inventory/get_inventory_key_link_options.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    inventory_id: inventoryId,
                    caster_id: ctx.caster_id ?? '',
                    location_id: ctx.location_id ?? '',
                    encounter_id: ctx.encounter_id ?? '',
                    campaign_id: ctx.campaign_id ?? '',
                    encounter_scope_id: opts.encounter_scope_id ?? '',
                    all_encounters: opts.all_encounters ? 1 : 0,
                    all_inventory: opts.all_inventory ? 1 : 0,
                }),
            });
            if (!res.ok) return;
            const data = recordOf(await res.json()) as KeyLinkResponse | null;
            const wrap = document.getElementById('div_inventory_key_links_wrap');
            if (!wrap || !data || !data.show_links) return;
            await compileLinkPickerInputs(data);
            renderKeyLinkInputs(data);
            data.all_inventory = !!opts.all_inventory;
            wrap.innerHTML = keyLinksMarkup(data);
            const host = inventoryEditHost();
            if (typeof host.initializeTomSelect === 'function') { try { host.initializeTomSelect(); } catch (e) { /* */ } }
            if (typeof host.ftMountIcons === 'function') { try { host.ftMountIcons(wrap); } catch (e) { /* */ } }
            const refetch = (): void => {
                const encSel = document.getElementById('inventory_key_link_encounter') as HTMLSelectElement | null;
                const allEncChk = document.getElementById('inventory_key_link_all_encounters') as HTMLInputElement | null;
                const allInvChk = document.getElementById('inventory_key_link_all_inventory') as HTMLInputElement | null;
                loadKeyLinkExtras(inventoryId, ctx, {
                    encounter_scope_id: encSel?.value ?? '',
                    all_encounters: !!(allEncChk && allEncChk.checked),
                    all_inventory: !!(allInvChk && allInvChk.checked),
                });
            };
            ['inventory_key_link_encounter', 'inventory_key_link_all_encounters', 'inventory_key_link_all_inventory'].forEach((cid) => {
                document.getElementById(cid)?.addEventListener('change', refetch);
            });
        } catch (e) { /* non-fatal — form works without the picker */ }
    })();
}

/**
 * For an encounter-possessed item, fetch the server-only extras (cross-encounter link options + map X/Z
 * bounds — neither is in the client cache) and inject them. Fire-and-forget: the rest of the form already
 * works (the X/Z sliders render synchronously with default bounds + stored values, refined here).
 */
function loadEncounterExtras(inventoryId: string, ctx: InventoryEditCtx): void {
    void (async () => {
        try {
            const res = await fetch(`${BASE_URL}options/tools/inventory/get_inventory_encounter_link_options.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    inventory_id: inventoryId,
                    caster_id: ctx.caster_id ?? '',
                    location_id: ctx.location_id ?? '',
                    encounter_id: ctx.encounter_id ?? '',
                    campaign_id: ctx.campaign_id ?? '',
                }),
            });
            if (!res.ok) return;
            const data = recordOf(await res.json());
            if (data) {
                await compileLinkPickerInputs(data);
                renderEncounterLinkInputs(data);
            }
            const bounds = recordOf(data?.bounds);
            if (data?.show_map_position && bounds) {
                setSliderBounds('inventory_x_pos', bounds.min_x, bounds.max_x);
                setSliderBounds('inventory_z_pos', bounds.min_z, bounds.max_z);
            }
            const wrap = document.getElementById('div_inventory_encounter_links_wrap');
            const links = asRecords(data?.links);
            if (wrap && data?.show_links && links.length) {
                wrap.innerHTML = encounterLinksMarkup(links);
                const host = inventoryEditHost();
                if (typeof host.initializeTomSelect === 'function') { try { host.initializeTomSelect(); } catch (e) { /* */ } }
                if (typeof host.ftMountIcons === 'function') { try { host.ftMountIcons(wrap); } catch (e) { /* */ } }
            }
        } catch (e) { /* non-fatal — form works without the picker */ }
    })();
}

/**
 * Build the Edit Inventory form from the cached inventory node. PHP checks actor permission;
 * prepareInventoryEditValues decides vitality and arc values before saving. Caller hydrates controls.
 */
export function renderEditInventoryForm(inventoryArr: unknown, container: HTMLElement, ctx: InventoryEditCtx = {}): void {
    const inventoryRecord = recordOf(inventoryArr);
    const inventoryId = inv(inventoryArr, 'inventory_id');
    const equipped = inv(inventoryArr, 'equipped');
    const isEquipped = equipped !== '' && equipped !== '0';
    const itemQty = inv(inventoryArr, 'item_qty') || '1';
    const inspectionDc = inv(inventoryArr, 'inventory_inspection_dc') || '10';
    const isPrivate = inv(inventoryArr, 'inventory_private') === '1';
    const unidentified = inv(inventoryArr, 'inventory_unidentified') === '1';
    const isWares = inv(inventoryArr, 'inventory_wares') === '1';
    const templateId = inv(inventoryArr, 'inventory_item_id');
    // Item-template <select> LABEL must be the TEMPLATE name (e.g. "Glasses"), NOT the instance
    // display name. On the standalone item.php path, ft_object_api_hydrate overwrites
    // items.item_name with inventory_name ("Onyx Glasses of True Sight"), so itemsCol(item_name)
    // returns the instance name here — wrong for the template picker. Resolve the template identity
    // from the catalog by inventory_item_id first (hydrate interns it on that path; the caster/
    // encounter path already carries it), falling back to itemsCol for any blob without a catalog hit.
    const templateName = String(catalogIdentity(inventoryArr, templateId)?.item_name
        ?? itemsCol(inventoryArr, 'item_name') ?? '');
    const languageId = inv(inventoryArr, 'inventory_written_in_language');
    const computed = recordOf(inventoryRecord?.computed);
    const readVitality = createVitalityReader({ resolveInventoryInstance: id => recordOf(inventoryEditHost().ftFindFullNestedItem?.(id)) });
    const catalog = recordOf(inventoryRecord?.catalog);
    const template = recordOf(catalog?.[templateId] ?? catalog?.[templateId.toUpperCase()] ?? catalog?.[templateId.toLowerCase()])
        ?? recordOf(inventoryEditHost().ftFindFullNestedItem?.(templateId));
    const vitalityNode = inventoryRecord && template
        ? { ...inventoryRecord, catalog: { ...catalog, [templateId]: template } } : inventoryRecord;
    const vit = recordOf(readVitality(vitalityNode)) ?? {};
    const templateMax = inventoryTemplateVitalityMaximum(template);
    // Hydrate sparse instance/template vitality before painting controls and their saved baseline.
    const vMax = Number(vit.v_max ?? vit.base_v_max ?? 0);
    const vMaxClamped = vMax < 0 ? 0 : vMax;
    const vCurrent = Math.min(Math.max(0, Number(vit.v_current ?? vit.base_v_current ?? 0)), vMaxClamped);
    const canCampaign = hasCampaignPermission(inventoryArr);
    // Position lives in the `position` bucket (posCoord), NOT the `inventory` bucket — reading it with
    // inventoryField returned undefined → 0, so a Save (which posts #inventory_x_pos / #inventory_z_pos)
    // shifted the marker/door to the origin. posCoord reads position/pos_arr/flat with the descalar guard.
    const xPos = posCoord(inventoryArr, 'x_pos') ?? 0;
    const zPos = posCoord(inventoryArr, 'z_pos') ?? 0;
    const root = BASE_URL;

    const itemType = String(itemsCol(inventoryArr, 'item_type') ?? '').toLowerCase();
    const isMapMarker = itemType === 'map_marker';
    // possessor_type moved to computed.possessor.type (bucket); read it first, fall back to legacy top-level.
    const possessorRaw = recordOf(computed?.possessor)?.type ?? inventoryRecord?.possessor_type;
    const possessorType = String((Array.isArray(possessorRaw) ? possessorRaw[0] : possessorRaw) || '').toLowerCase();
    // Cache-rollover fallback for caster blobs compiled before PC inventory was stamped as
    // player_character: show Wares only when this is a DIRECT possession and the form caster is a
    // cached PC. Nested items intentionally remain excluded from the edit form.
    const formCasterId = String(ctx.caster_id ?? '').trim();
    const directPossessId = inv(inventoryArr, 'possess_id');
    const formCaster = formCasterId
        ? inventoryEditHost().FateTide?.casterCache?.get(formCasterId)
        : undefined;
    const formCasterRecord = recordOf(formCaster);
    const formCasterPcBucket = recordOf(formCasterRecord?.player_characters);
    const directContextPcOwned = formCasterId !== ''
        && directPossessId.toUpperCase() === formCasterId.toUpperCase()
        && !!formCasterPcBucket
        && Object.keys(formCasterPcBucket).length > 0;
    const waresEligible = possessorType === 'player_character' || directContextPcOwned;
    const isEncounterPossessed = possessorType === 'encounter';
    const supportsLockpick = lockpickSupported(inventoryArr);

    // map_marker: no Item-Template picker (locked to the marker item); hidden #inventory_item_id only.
    const itemTemplateControl = isMapMarker
        ? `<input id="inventory_item_id" type="hidden" value="${esc(templateId)}">`
        : `
    <div class="text-left mb-3">${dynamicSelect({ id: 'inventory_item_id', itemType: 'object', preselectValue: templateId, preselectLabel: templateName, gated: true, iconTooltip: 'Item Template' })}</div>`;

    const lockpickControl = supportsLockpick ? lockpickArc(inventoryArr) : '';

    // Map X/Z: encounter-possessed items get sliders (bounds refined async from the endpoint, stored value
    // present immediately so a save before that fetch never resets position); everything else stays hidden.
    const xzControl = isEncounterPossessed
        ? `
    <hr class=" border-1 my-2" />
    <div class="text-left mb-2">${slider('inventory_x_pos', 'X Position', 'x_pos', -100, 100, 0.001, xPos, undefined, 'ftSyncFt3dInventoryPositionFromForm(this)')}</div>
    <div class="text-left mb-2">${slider('inventory_z_pos', 'Z Position', 'z_pos', -100, 100, 0.001, zPos, undefined, 'ftSyncFt3dInventoryPositionFromForm(this)')}</div>`
        : `
    <input type="hidden" id="inventory_x_pos" value="${esc(xPos)}">
    <input type="hidden" id="inventory_z_pos" value="${esc(zPos)}">`;

    const qtyControl = isEquipped
        ? `<input id="item_qty" type="hidden" value="${esc(itemQty)}">`
        : `
        <div class="text-left mb-3">
            <div class="d-flex align-items-center gap-2">
                <span class="flex-shrink-0 d-inline-flex align-items-center">${icon('qty', 25, 25, true)}</span>
                <input id="item_qty" class="form-control form-control-sm flex-grow-1" type="number" placeholder="Qty"
                    min="1" max="99999" value="${esc(itemQty)}" data-ft-modify-inventory="1">
            </div>
        </div>`;

    const inspectionControl = canCampaign
        ? `
        <div class="text-left mb-2">
            <div class="form-row d-flex align-items-center">
                <div class="pr-2">${icon('inspection')}</div>
                <div>
                    <input id="inventory_inspection_dc" class="form-control" type="number" value="${esc(inspectionDc)}"
                        min="0" max="100" step="1" data-ft-modify-inventory="1" data-ft-modify-inventory-input="1">
                </div>
            </div>
        </div>`
        : `<input id="inventory_inspection_dc" type="hidden" value="${esc(inspectionDc)}">`;

    const html = `
<div class="row gx-0 inventory-container-form" data-inventory-enchantment-valid="1">

    <input id="inventory_id" type="hidden" value="${esc(inventoryId)}">
    <input id="input_hidden_sidelog_object_item_id" type="hidden" value="${esc(inventoryId)}">

    <div class="text-center mb-3 mt-2">
        <div><h3>${icon('edit_inventory', 40, 40)} Edit Inventory</h3></div>
    </div>

    <div class="d-flex align-items-center">
        <div class="p-2">${hideButtons(inventoryId, readInventoryHidden(recordOf(inventoryArr), HIDDEN_STATUS_ID) === 1, String(directPossessId ?? ''), String(ctx.encounter_id ?? ''))}</div>
        <div class="p-2">${privateButtons(inventoryId, isPrivate)}</div>
        <div class="mx-auto p-2">
            <button class="btn btn-danger btn-sm" id="btn_delete_inventory_${esc(inventoryId)}"
                type="button"
                data-ft-inventory-action="delete"
                data-ft-inventory-id="${esc(inventoryId)}"
                data-ft-campaign-id="${esc(ctx.campaign_id ?? '')}"
                data-ft-encounter-id="${esc(ctx.encounter_id ?? '')}"
                data-ft-caster-id="${esc(ctx.caster_id ?? '')}">
                ${icon('delete_inventory')}
            </button>
        </div>
    </div>

    <div class="text-left mb-3">${textFieldFlat('inventory_name', inv(inventoryArr, 'inventory_name'), 'inventory_name', 'Inventory Name')}</div>

    ${itemTemplateControl}

    <div class="text-left mb-3">${textFieldFloating('inventory_cost_gp', inv(inventoryArr, 'inventory_cost_gp'), 'override_cost_gp')}</div>

    ${qtyControl}

    <div class="text-left mb-3">${checkbox('inventory_hidden', 'hidden', 'Hidden', readInventoryHidden(recordOf(inventoryArr), HIDDEN_STATUS_ID) === 1)}</div>

    <div class="text-left mb-3">${checkbox('inventory_unidentified', 'unidentified', 'Unidentified', unidentified)}</div>

    ${waresEligible ? `<div class="text-left mb-3">${checkbox('inventory_wares', 'merchant', 'Wares (mark to sell at merchants)', isWares)}</div>` : ''}

    ${inspectionControl}

    <div class="text-left mb-3">
        <div class="text-left mb-2">
            <a class="fw-bold link-primary" href="${root}enchantment_templates.php" target="_blank"><u>Enchantment Template ID:</u></a>
            ${icon('enchantment_template_help')}
        </div>
        <div class="text-left">
            <div class="d-flex align-items-center gap-2">
                <a class="flex-shrink-0 link-primary d-inline-flex align-items-center" href="${root}enchantment_templates.php" target="_blank" rel="noopener noreferrer" title="Open enchantment templates">${icon('item_enchanted', 25, 25, true)}</a>
                <div class="flex-grow-1 min-w-0">
                    <input type="text" id="enchantment_template_id" class="form-control" maxlength="36" placeholder="Enchantment Template ID"
                        value="${esc(inv(inventoryArr, 'enchantment_template_id'))}" data-ft-open-item-info-value-on-blur="1" data-ft-modify-inventory="1" autocomplete="off">
                </div>
            </div>
        </div>
        <div id="div_enchantment_template_meta" class="small mt-1 d-none" data-ft-inventory-enchant-meta>
            <span class="text-muted font-monospace user-select-all d-block lh-sm mb-1" data-ft-inventory-enchant-meta-uuid></span>
            <a class="link-info link-underline-opacity-25 link-underline-opacity-100-hover" href="#" target="_blank" rel="noopener noreferrer" data-ft-inventory-enchant-meta-link></a>
        </div>
        <div id="div_enchantment_template_warning" class="mb-2" style="display:none"></div>
    </div>

    <div class="text-left mb-3">${dynamicSelect({ id: 'inventory_written_in_language', itemType: 'language', preselectValue: languageId, preselectLabel: languageNameById(languageId) || languageId, gated: false })}</div>

    <div class="text-left mb-3">${quillTextarea('inventory_description', inv(inventoryArr, 'inventory_description'))}</div>

    <div id="div_inventory_description_script_preview_wrap" class="text-start mb-3" style="display: none;">
        <div class="form-label small text-muted mb-1">Written script preview (cipher + font; plain text from the editor, not full rich-text or roll buttons)</div>
        <div id="div_inventory_description_script_preview" class="border rounded p-2 bg-body-secondary small overflow-auto" style="max-height: 12rem;"></div>
    </div>

    <div class="text-left mb-3">${quillTextarea('inventory_flavor_text', inv(inventoryArr, 'inventory_flavor_text'))}</div>

    <div class="text-left mb-3">${quillTextarea('inventory_gm_notes', inv(inventoryArr, 'inventory_gm_notes'))}</div>

    <hr class=" border-1 my-2" />

    <div class="text-left mb-2 inventory-v-slider-wrap">
        ${slider('inventory_v_current', '', 'v_current', 0, vMaxClamped, 1, vCurrent, 'inventory_modify_v_slider_max_label')}
    </div>

    <div class="text-left mb-2">
        <div class="d-flex align-items-center gap-2">
            <div class="d-flex align-items-center flex-grow-1" style="min-width:0;">
                <span class="pr-1 flex-shrink-0">${icon('v_max', 25, 25, true, 'V Max')}</span>
                <input id="inventory_v_max" class="form-control form-control-sm" type="number" value="${esc(vMax)}" min="0" step="1" data-ft-modify-inventory="1">
            </div>
            <div class="d-flex align-items-center flex-grow-1" style="min-width:0;">
                <span class="pr-1 flex-shrink-0">${icon('v_temp', 25, 25, true, 'V Temp')}</span>
                <input id="inventory_v_temp" class="form-control form-control-sm" type="number" value="${esc(Number(vit.v_temp ?? 0))}" min="0" step="1" data-ft-modify-inventory="1">
            </div>
            <div class="d-flex align-items-center flex-grow-1" style="min-width:0;">
                <span class="pr-1 flex-shrink-0">${icon('v_max_temp', 25, 25, true, 'V Max Temp')}</span>
                <input id="inventory_v_max_temp" class="form-control form-control-sm" type="number" min="0" step="1" value="${esc(Number(vit.v_max_temp ?? 0))}" data-ft-modify-inventory="1">
            </div>
        </div>
    </div>

    ${lockpickControl}

    ${xzControl}

    <!-- Encounter-link picker (+ X/Z bound refinement) for encounter-possessed items — injected async
         from get_inventory_encounter_link_options.php (server-only cross-encounter data). -->
    <div id="div_inventory_encounter_links_wrap"></div>

    <!-- Key ↔ lock link picker ("Locks this key opens") for item_type='key' rows — injected async
         from get_inventory_key_link_options.php (scoped instance search). -->
    <div id="div_inventory_key_links_wrap"></div>

    <div id="div_refresh_side_form"></div>
</div>`;

    container.innerHTML = html;
    const form = container.querySelector<HTMLElement>('.inventory-container-form');
    if (form) saveInventoryVitalityBaseline(form, {
        v_current: vCurrent, v_max: vMax,
        v_temp: Number(vit.v_temp ?? vit.base_v_temp ?? 0),
        v_max_temp: Number(vit.v_max_temp ?? vit.base_v_max_temp ?? 0), templateMax, templateId,
    });

    // The vitality slider/max coupling is normally wired by an inline <script> in the PHP form, but
    // scripts injected via innerHTML don't execute — bind it programmatically (no-op if unavailable).
    try {
        inventoryEditHost().ftBindVitalitySliderToVMax?.(
            'inventory_v_max', 'inventory_v_current', 'inventory_modify_v_slider_max_label',
        );
    } catch (e) { /* non-fatal */ }

    // Lockpick dual-range wiring (replaces the PHP form's inline <script>).
    if (supportsLockpick) {
        try { bindLockpickArc(); } catch (e) { /* non-fatal */ }
    }

    // Encounter-possessed items: pull the server-only extras (link picker + X/Z bounds) and inject them.
    if (isEncounterPossessed) {
        try { loadEncounterExtras(inventoryId, ctx); } catch (e) { /* non-fatal */ }
    }

    // Keys: pull the scoped "Locks this key opens" picker.
    if (itemType === 'key') {
        try { loadKeyLinkExtras(inventoryId, ctx); } catch (e) { /* non-fatal */ }
    }
}

// Vitality fields live under computed.vitality; position fields under the `position` bucket (the
// canonical posCoord source); everything else is an `inventory`-bucket column.
const VITALITY_FIELDS = new Set(['v_current', 'v_max', 'v_temp', 'v_max_temp']);
const POSITION_FIELDS = new Set(['x_pos', 'y_pos', 'z_pos']);

/**
 * Optimistically patch an encounter inventory position in the retained FT3D projection.
 */
export function syncFt3dInventoryPosition(inventoryId: string, xPos?: number, zPos?: number): void {
    try {
        const cache = inventoryEditHost().FateTide?.encounterCache;
        if (!cache || !inventoryId) return;
        const upperId = String(inventoryId).toUpperCase();
        cache.forEach((encounterValue) => {
            const encounter = recordOf(encounterValue);
            const list = encounter?.encounter_inventories_arr;
            const items = asRecords(list);
            const entry = items.find(
                (i) => String(inventoryField(i, 'inventory_id') ?? '').toUpperCase() === upperId,
            );
            if (!entry) return;
            const x = Number(xPos ?? posCoord(entry, 'x_pos') ?? 0);
            const z = Number(zPos ?? posCoord(entry, 'z_pos') ?? 0);
            if (!Number.isFinite(x) || !Number.isFinite(z)) return;
            const position = recordOf(entry.position) ?? (entry.position = {} as InventoryRecord);
            position.x_pos = x;
            position.z_pos = z;
            window.dispatchEvent(new CustomEvent('ft:encounter-state-received', {
                detail: { encounterArr: encounter, source: 'inventory-position-edit' },
            }));
        });
    } catch (e) { /* non-fatal — the save + websocket reconcile remain authoritative */ }
}

/**
 * Slider oninput hook: read the live Edit Inventory form's id + X/Z and push them onto the map every
 * drag tick. `el` is the input that fired (closest .inventory-container-form scopes it to this form).
 */
export function syncFt3dInventoryPositionFromForm(el?: HTMLElement): void {
    try {
        const form = (el && el.closest('.inventory-container-form'))
            || document.querySelector('.inventory-container-form');
        if (!form) return;
        const id = (form.querySelector('#inventory_id') as HTMLInputElement | null)?.value || '';
        if (!id) return;
        const x = parseFloat((form.querySelector('#inventory_x_pos') as HTMLInputElement | null)?.value ?? '');
        const z = parseFloat((form.querySelector('#inventory_z_pos') as HTMLInputElement | null)?.value ?? '');
        syncFt3dInventoryPosition(
            id,
            Number.isFinite(x) ? x : undefined,
            Number.isFinite(z) ? z : undefined,
        );
    } catch (e) { /* non-fatal */ }
}

/**
 * Optimistic surgical cache update after an inventory save: patch the just-edited fields into the
 * cached full-nest inventory node IN PLACE (the same object the side-info card + sheet render from),
 * then repaint the carrying caster from cache so the Side Info card AND the sheet inventory tile reflect
 * the change immediately. Saved-row delivery later reconciles the same held state.
 *
 * `fields` is the raw {name → value} set modifyInventory() already gathered from the form. Returns true
 * when the node was found + patched. Unknown ids / no cache → false (caller can fall back).
 */
export function applyInventoryEditToCache(inventoryId: string, fields: Record<string, unknown>): boolean {
    // Encounter-possessed items are not necessarily present in the caster cache, so patch the
    // retained FT3D encounter projection independently.
    if ('x_pos' in fields || 'z_pos' in fields) {
        const xv = fields.x_pos, zv = fields.z_pos;
        syncFt3dInventoryPosition(
            inventoryId,
            xv === undefined ? undefined : Number(xv),
            zv === undefined ? undefined : Number(zv),
        );
    }
    try {
        const host = inventoryEditHost();
        const cache = host.FateTide?.casterCache;
        let node = recordOf(host.ftFindFullNestedItem?.(inventoryId));
        let catalog = recordOf(node?.catalog);
        for (const held of host.FateTide?.encounterCache?.values() ?? []) {
            const encounter = recordOf(held);
            const floor = asRecords(encounter?.encounter_inventories_arr).find(row =>
                String(inventoryField(row, 'inventory_id')).toUpperCase() === inventoryId.toUpperCase());
            if (!floor) continue;
            node ??= floor; catalog ??= recordOf(encounter?.catalog);
            break;
        }
        if (!node) return false;

        const inventory = recordOf(node.inventory) ?? (node.inventory = {});
        const vitality: ResourceValues = {};
        for (const key of Object.keys(fields)) {
            const val = fields[key];
            if (val === undefined) continue;
            if (VITALITY_FIELDS.has(key)) {
                vitality[key as ResourceColumn] = Number(val);
            } else if (POSITION_FIELDS.has(key)) {
                // posCoord reads `position` first — patch it there so the live map-token / side-info
                // repaint reflects the drag/edit instead of reading the stale bucket.
                const position = recordOf(node.position) ?? (node.position = {});
                position[key] = val;
            } else {
                inventory[key] = val;
            }
        }

        if (Object.keys(vitality).length) applyResourceValues(node, vitality, undefined, null, catalog ?? undefined);

        // Find which cached caster carries this inventory id, then run the same side effects a
        // caster_update would (sheet panes + open side-info card), from the now-patched cache.
        const upperId = String(inventoryId).toUpperCase();
        let casterId: string | null = null;
        for (const [key, blob] of cache?.entries() ?? []) {
            const ids = typeof host.ftCollectInventoryIds === 'function' ? host.ftCollectInventoryIds(blob) : [];
            if (Array.isArray(ids) && ids.indexOf(upperId) !== -1) { casterId = key; break; }
        }
        if (casterId && typeof host.ftRepaintCasterFromCache === 'function') {
            host.ftRepaintCasterFromCache(casterId);
        } else if (typeof host.ftRerenderOpenSideInfo === 'function') {
            // No carrying caster in cache (e.g. a loose object-cache item) — at least repaint the card.
            host.ftRerenderOpenSideInfo([inventoryId]);
        }

        // DIRECT side-info repaint (bypasses the isSideInfoPanelOpen() layout gate that blocks both
        // ftRepaintCasterFromCache's side-info branch AND ftRerenderOpenSideInfo). The sheet repaint
        // above has no such gate, which is why the sheet was updating but the side-info card was not.
        // We KNOW the user is actively editing THIS item, so if the drawer is registered as showing it
        // (ftGetOpenSideInfoId), re-render that card straight from the patched cache.
        try {
            const openId = typeof host.ftGetOpenSideInfoId === 'function' ? host.ftGetOpenSideInfoId() : null;
            if (openId && String(openId).toUpperCase() === upperId) {
                const sideEl = document.getElementById('div_refresh_side_info');
                if (sideEl && typeof host.ftRenderItemFromCache === 'function') {
                    host.ftRenderItemFromCache(inventoryId, sideEl);
                }
            }
        } catch (e) { /* non-fatal */ }

        return true;
    } catch (e) {
        return false;
    }
}

// Element-id → cache-field-key map for the live single-field patch (vitality + position ids differ
// from their inventory-bucket / computed.vitality keys; everything else is identity).
const LIVE_FIELD_KEY: Record<string, string> = {
    inventory_v_current: 'v_current',
    inventory_v_max: 'v_max',
    inventory_v_temp: 'v_temp',
    inventory_v_max_temp: 'v_max_temp',
    inventory_x_pos: 'x_pos',
    inventory_z_pos: 'z_pos',
};

/**
 * LIVE field edit (oninput) — patch the cached node for the just-typed field and repaint the side-info
 * card + sheet tile IMMEDIATELY (every keystroke), so the edit feels live instead of only landing after
 * the debounced server save. The actual DB save still goes through the existing debounced
 * modifyInventory(event) (500ms) — this only makes the on-screen reflection instant.
 */
export function liveInventoryEdit(el: HTMLInputElement): void {
    try {
        const host = inventoryEditHost();
        const container = el.closest('.inventory-container-form');
        const invId = container ? (container.querySelector('#inventory_id') as HTMLInputElement | null)?.value : '';
        if (invId && typeof host.ftApplyInventoryEditToCache === 'function') {
            const key = LIVE_FIELD_KEY[el.id] || el.id;
            const val: unknown = el.type === 'checkbox' ? (el.checked ? 1 : 0) : el.value;
            host.ftApplyInventoryEditToCache(invId, { [key]: val });
        }
    } catch (e) { /* non-fatal — the debounced save below is the source of truth */ }
    // Debounced server save (unchanged path).
    try {
        const host = inventoryEditHost();
        if (typeof host.debounceInput === 'function') host.debounceInput(el, 'modifyInventory(event)');
    } catch (e) { /* non-fatal */ }
}

function closestInventoryEditTarget(event: Event, selector: string): HTMLElement | null {
    return event.target instanceof Element ? event.target.closest<HTMLElement>(selector) : null;
}

function callWindow(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as Record<string, unknown>)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

async function handleInventoryAction(el: HTMLElement): Promise<void> {
    const action = el.dataset.ftInventoryAction || '';
    const inventoryId = el.dataset.ftInventoryId || '';
    if (!action || !inventoryId) return;

    if (action === 'reveal' || action === 'hide') {
        const desired: Bit = action === 'hide' ? 1 : 0;
        const possessId = el.dataset.ftPossessId || '';
        const encounterId = el.dataset.ftEncounterId || '';
        // The websocket bundle's helper (one shared per-row queue with the scene's frames); else the same engine queue directly.
        const shared = (window as unknown as { ftSetInventoryHidden?: (id: string, d: Bit, p: string, e?: string) => Promise<boolean> }).ftSetInventoryHidden;
        if (typeof shared === 'function') { await shared(inventoryId, desired, possessId, encounterId); return; }
        const paint = (value: Bit): void => {
            const revealBtn = document.getElementById(`btn_reveal_creature_${inventoryId}`);
            const hideBtn = document.getElementById(`btn_hide_creature_${inventoryId}`);
            if (revealBtn) revealBtn.style.display = value === 1 ? 'block' : 'none';
            if (hideBtn) hideBtn.style.display = value === 1 ? 'none' : 'block';
            const form = el.closest('.inventory-container-form');
            const checkbox = form?.querySelector<HTMLInputElement>('#inventory_hidden');
            if (checkbox) checkbox.checked = value === 1;
        };
        const gen = (editHiddenGeneration.get(inventoryId) ?? 0) + 1;
        editHiddenGeneration.set(inventoryId, gen);
        const answer = await queueInventoryFlag({ kind: 'hidden', encounterId, inventoryId, desired, seed: { value: desired === 1 ? 0 : 1, possessId },
            onPrepared: () => { if (editHiddenGeneration.get(inventoryId) === gen) paint(desired); } })
            .catch((error: unknown) => ({ ok: false, status: 0, error: error instanceof Error ? error.message : String(error), saved: undefined }));
        if (answer.ok) return;
        if (editHiddenGeneration.get(inventoryId) === gen) paint(answer.saved ? answer.saved.value : (desired === 1 ? 0 : 1));
        callWindow('displayWarning', answer.status === 403 ? 'Only the GM can hide or reveal this object.' : 'The change could not be saved: ' + String(answer.error || answer.status));
        return;
    }

    if (action === 'make-private' || action === 'make-public') {
        const makePrivate = action === 'make-private';
        await callWindow(
            'queryData',
            `${BASE_URL}options/db_queries/update/update_privacy_inc.php`,
            'div_sidelog_warning',
            {
                item_type: 'inventory',
                inventory_id: inventoryId,
                inventory_private: makePrivate ? '1' : '0',
            },
        );
        const publicBtn = document.getElementById(`inventory_public_${inventoryId}`);
        const privateBtn = document.getElementById(`inventory_private_${inventoryId}`);
        if (publicBtn) publicBtn.style.display = makePrivate ? 'none' : 'block';
        if (privateBtn) privateBtn.style.display = makePrivate ? 'block' : 'none';
        return;
    }

    if (action === 'delete') {
        const bridge = window as Window & { ftTryDeleteInventory?: (input: { inventoryId: string; encounterId: string; actorId: string; log: boolean }) => Promise<'handled' | 'refused'> };
        if (!bridge.ftTryDeleteInventory) { callWindow('displayWarning', 'inventory-delete:browser-required'); return; }
        const result = await bridge.ftTryDeleteInventory({ inventoryId, encounterId: el.dataset.ftEncounterId || '',
            actorId: el.dataset.ftCasterId || '', log: !encounterAuthoringActive() });
        if (result !== 'handled') return;
        callWindow('closeSideInfoPanel');
        callWindow('openSideLog');
    }
}

document.addEventListener('click', (event) => {
    const action = closestInventoryEditTarget(event, '.inventory-container-form [data-ft-inventory-action]');
    if (!action) return;
    event.preventDefault();
    void handleInventoryAction(action);
});

document.addEventListener('input', (event) => {
    const liveEdit = closestInventoryEditTarget(event, '.inventory-container-form [data-ft-live-inventory-edit]');
    if (liveEdit instanceof HTMLInputElement) {
        liveInventoryEdit(liveEdit);
        return;
    }

    const mirror = closestInventoryEditTarget(event, '.inventory-container-form [data-ft-mirror-target]');
    if (mirror instanceof HTMLInputElement) {
        const target = document.getElementById(mirror.dataset.ftMirrorTarget || '') as HTMLInputElement | null;
        if (target) target.value = mirror.value;
        if (mirror.dataset.ftSyncXz === '1') syncFt3dInventoryPositionFromForm(mirror);
        return;
    }

    const modifyInput = closestInventoryEditTarget(event, '.inventory-container-form [data-ft-modify-inventory-input]');
    if (modifyInput) {
        callWindow('modifyInventory', event);
    }
});

document.addEventListener('change', (event) => {
    const debounced = closestInventoryEditTarget(event, '.inventory-container-form [data-ft-debounce-modify-inventory]');
    if (debounced) {
        callWindow('debounceInput', debounced, 'modifyInventory(event)');
        return;
    }

    const modify = closestInventoryEditTarget(event, '.inventory-container-form [data-ft-modify-inventory]');
    if (modify) {
        callWindow('modifyInventory', event);
    }
});

document.addEventListener('blur', (event) => {
    const openInfoValue = closestInventoryEditTarget(event, '.inventory-container-form [data-ft-open-item-info-value-on-blur]');
    if (openInfoValue) {
        callWindow('openItemInfoValue');
    }
}, true);

Object.assign(window, {
    renderEditInventoryForm,
    ftPrepareInventoryEditValues: prepareInventoryEditValues,
    ftApplyInventoryEditToCache: applyInventoryEditToCache,
    ftLiveInventoryEdit: liveInventoryEdit,
    ftSyncFt3dInventoryPosition: syncFt3dInventoryPosition,
    ftSyncFt3dInventoryPositionFromForm: syncFt3dInventoryPositionFromForm,
});
